import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import {FormattedMessage, injectIntl} from 'react-intl';

import {Drawer} from '@stubhub/bamboo';
import LegacyCache from '@stubhub/legacy-cache';
import CalendarPanel from '@stubhub/react-calendar-panel';

import '../styles/index.scss';

export const DATE_FILTER = 'date-filter';

const formatDate = (selecteddate, globalRegistry) => {
  const currentLocale = globalRegistry.getCurrentLocale();
  const date = new Date(selecteddate);
  const options = {month: 'short', day: 'numeric', year: 'numeric'};

  return date.toLocaleDateString(currentLocale, options);
};
const DEFAULT_WINDOW_WIDTH = 600;
const SMALL_WINDOW_WIDTH = 640;
const windowWidth = __CLIENT__ ? window.innerWidth : DEFAULT_WINDOW_WIDTH;
const isSmall = windowWidth < SMALL_WINDOW_WIDTH;

class DatePicker extends React.Component {
  constructor(props, context) {
    super(props, context);
    const {from, to, selectionType} = this.props;
    const initialDate = from ? from : new Date();
    // Initialize legacy cache
    this.legacyCache = new LegacyCache({
      type: 'session',
      namespace: DATE_FILTER,
    });

    this.state = {
      from: initialDate,
      to,
      selectionType: selectionType || 'from',
    };
  }

  handleChangeTo = () => {
    this.switchSelection('to');
  };

  handleChangeFrom = () => {
    this.switchSelection('from');
  };

  selectedDate = (date) => {
    let {from, to, selectionType} = this.state;
    const {onSelection} = this.props;
    const prevSelectedDate = this.legacyCache.get('dateFilterCacheSettings');
    let prevSelectedtoDate;
    let prevSelectedfromDate;

    if (prevSelectedDate) {
      prevSelectedfromDate = new Date(prevSelectedDate.eventStartDate);
      prevSelectedtoDate = new Date(prevSelectedDate.eventEndDate);
    }
    if (selectionType === 'single') {
      onSelection?.(new Date(date));
      this.setState({from: date});
    } else {
      if (selectionType === 'from') {
        from = date;
        if (prevSelectedtoDate && from < prevSelectedtoDate) {
          to = prevSelectedtoDate;
        }
        if (to && from.getTime() > to.getTime()) {
          to = undefined;
        }
        if (to === undefined) {
          selectionType = 'to';
        }
      } else {
        this.setState({from, to, selectionType});
        to = date;
        if (from === undefined && prevSelectedfromDate) {
          from = prevSelectedfromDate;
        } else if (from === undefined) {
          from = new Date(Date.now());
        }
      }

      if (from && to) {
        const dateFilterSettings = {
          prefDate: 'custom_date_range',
          eventStartDate: from,
          eventEndDate: to,
          allDates: false,
          isDateSelected: true
        };
        this.legacyCache.unlockNamespace();
        this.legacyCache.set('dateFilterCacheSettings', dateFilterSettings);
        onSelection?.(from, to);
      }
      this.setState({from, to, selectionType});
      this.setState({from, to, selectionType});
    }
  };

  switchSelection = (type) => {
    this.setState({selectionType: type});
  };

  /**
   * Method to handle document click which hides items when click outside current dropdownlist.
   * @param e [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   */
  handleDocumentClick = (e) => {
    const { onClose } = this.props;
    if (this.rootNode && !this.rootNode.contains(e.target)) {
      e.focusout = true;
      onClose?.(e);
    }
  };

  onStop = (e) => {
    this.handleDocumentClick(e);
  };

  // Don't use draggable if viewport is not small
  useDraggable = (draggable) => {
    return draggable && isSmall;
  };

  /**
   * React Component Lifecycle Methods.
   *
   */
  componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  render() {
    const {draggable: propsDraggable = false, triggerComponent} = this.props;
    const draggable = this.useDraggable(propsDraggable);

    return (
      <div className={cs('DatePicker__DPC1', {Draggable: draggable})}>
        <div className="DatePicker__DPC2">
          {draggable ? (
            <Drawer
              className="DatePicker__Drawer"
              onReleaseDrawer={this.onStop}
              triggerComponent={triggerComponent}
              defaultOpen
            >
              {this.inRender()}
            </Drawer>
          ) : (
            <div className="draggablePlaceHolder">{this.inRender()}</div>
          )}
        </div>
      </div>
    );
  }

  inRender() {
    let {from, to} = this.state;
    const {selectionType} = this.state;
    let selectedYear;
    let selectedMonth;
    const date = new Date(Date.now());
    const dateAvailable = this.legacyCache.get('dateFilterCacheSettings');
    const { draggable: propsDraggable, onClose } = this.props;
    const { globalRegistry } = this.context;
    const draggable = this.useDraggable(propsDraggable);

    if (!from && !to && dateAvailable) {
      from = dateAvailable.eventStartDate;
      to = dateAvailable.eventEndDate;
    }
    if (from !== undefined && to === undefined && selectionType === 'to') {
      selectedMonth = new Date(from).getMonth();
      selectedYear = new Date(from).getFullYear();
    } else if (selectionType === 'from' && dateAvailable) {
      selectedMonth = new Date(dateAvailable.eventStartDate).getMonth();
      selectedYear = new Date(dateAvailable.eventStartDate).getFullYear();
    } else if (selectionType === 'to' && dateAvailable) {
      selectedMonth = new Date(dateAvailable.eventEndDate).getMonth();
      selectedYear = new Date(dateAvailable.eventEndDate).getFullYear();
    } else if (selectionType === 'single' && from) {
      selectedMonth = new Date(from).getMonth();
      selectedYear = new Date(from).getFullYear();
    } else {
      selectedMonth = new Date(from).getMonth() || date.getMonth();
      selectedYear = new Date(from).getFullYear() || date.getFullYear();
    }

    return (
      <div
        className={cs('date-range-picker', {Draggable: draggable}, {'single-date-picker': selectionType === 'single'})}
        ref={(node) => (this.rootNode = node)}
      >
        <div className="drp-header">
          <div className="drp-header-text">
            {selectionType !== 'single' ? (
              <FormattedMessage
                id="DatePicker.header"
                defaultMessage="Select date range"
                description="Calendar Header"
              />
            ) : (
              <FormattedMessage
                id="DatePicker.header_single"
                defaultMessage="Select date"
                description="Calendar Header Single"
              />
            )}
          </div>
          <div className="sh-iconset-container" onClick={onClose}>
            {draggable ? null : <div className="sh-iconset sh-iconset-close close-options" />}
          </div>
        </div>
        {selectionType !== 'single' && (
          <div className="drp-from-to">
            <div
              className={`tab from-tab ${selectionType === 'from' ? 'current-tab' : ''}`}
              onClick={this.handleChangeFrom}
            >
              <div className="tab-title">
                <FormattedMessage id="DatePicker.from" defaultMessage="From" description="From Date" />
              </div>
              <div className="tab-text">{from ? formatDate(from, globalRegistry) : ''}</div>
            </div>
            <div className={`tab to-tab ${selectionType === 'to' ? 'current-tab' : ''}`} onClick={this.handleChangeTo}>
              <div className="tab-title">
                <FormattedMessage id="DatePicker.to" defaultMessage="To" description="To Date" />
              </div>
              <div className="tab-text">{to ? formatDate(to, globalRegistry) : ''}</div>
            </div>
          </div>
        )}
        <div className="DatePicker__MainContent">
          <CalendarPanel
            {...this.props}
            from={from}
            to={to}
            onselectDate={this.selectedDate}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            selectionType={selectionType}
          />
        </div>
      </div>
    );
  }
}

DatePicker.propTypes = {
  intl: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  selectionType: PropTypes.string,
  onSelection: PropTypes.func,
  draggable: PropTypes.bool,
  triggerComponent: PropTypes.node,
};

DatePicker.contextTypes = {
  globalRegistry: PropTypes.object,
  onClose: () => undefined,
};

export default injectIntl(DatePicker);
